import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ExternalLink,
  Flame,
  Loader2,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  Wallet,
  XCircle,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { short } from '../decode.js'
import { Mask } from './Mask.js'
import type { ZerionTransaction } from '../types.js'
import type { TransactionsState } from '../hooks/useTransactions.js'

interface Props {
  state: TransactionsState
  onLoadMore: () => void
  /** Address that owns the txs — used to label transfers as "from your Safe"
   *  vs "to your Safe" when the Zerion direction would be ambiguous. */
  selfAddress: string
}

export function TransactionsCard({ state, onLoadMore, selfAddress }: Props) {
  if (state.status === 'idle') return null
  if (state.status === 'error' && state.isUnavailable) return null

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Recent activity</CardTitle>
        {state.status === 'ready' && (
          <span className="text-xs text-muted-foreground">
            {state.txs.length} loaded
          </span>
        )}
      </CardHeader>
      <CardContent>
        <Body state={state} selfAddress={selfAddress} onLoadMore={onLoadMore} />
      </CardContent>
    </Card>
  )
}

function Body({
  state,
  selfAddress,
  onLoadMore,
}: {
  state: TransactionsState
  selfAddress: string
  onLoadMore: () => void
}) {
  if (state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading recent transactions…
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="text-sm text-destructive">
        Couldn't load transactions: {state.message}
      </div>
    )
  }

  if (state.status === 'ready' && state.txs.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No transactions found for this Safe yet.
      </div>
    )
  }

  if (state.status !== 'ready') return null

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-border">
        {state.txs.map((tx) => (
          <TransactionRow
            key={`${tx.attributes.hash}-${tx.id}`}
            tx={tx}
            selfAddress={selfAddress}
          />
        ))}
      </ul>
      {state.cursor && (
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={state.loadingMore}
          >
            {state.loadingMore ? (
              <>
                <Loader2 className="animate-spin" />
                Loading…
              </>
            ) : state.loadMoreError ? (
              'Retry'
            ) : (
              'Load more'
            )}
          </Button>
          {state.loadMoreError && (
            <span className="text-xs text-destructive">
              Couldn't load next page: {state.loadMoreError}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function TransactionRow({
  tx,
  selfAddress,
}: {
  tx: ZerionTransaction
  selfAddress: string
}) {
  const a = tx.attributes
  // Zerion is JSON:API but some operation_types omit relationships
  // entirely (or just the dapp side). Treat every level as optional.
  const chain = tx.relationships?.chain?.data?.id ?? 'ethereum'
  const dapp = tx.relationships?.dapp?.data?.id ?? null
  const failed = a.status !== 'confirmed'
  const explorerUrl = explorerLink(chain, a.hash)

  const summary = describeTx(tx, selfAddress)
  const time = formatRelative(a.mined_at)

  return (
    <li className="flex items-start gap-3 py-3 text-sm">
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
        <OperationIcon op={a.operation_type} failed={failed} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">
              <Mask as="***">{summary.title}</Mask>
            </span>
            {dapp && (
              <Badge variant="outline" className="font-normal">
                {dapp}
              </Badge>
            )}
            <Badge variant="secondary" className="font-normal text-[10px]">
              {prettyChain(chain)}
            </Badge>
            {failed && (
              <Badge className="border-destructive/40 bg-destructive/10 text-destructive">
                failed
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{time}</span>
        </div>
        {summary.detail && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            <Mask as="***">{summary.detail}</Mask>
          </div>
        )}
      </div>
      <a
        href={explorerUrl}
        target="_blank"
        rel="noopener"
        className="mt-1 text-muted-foreground hover:text-foreground"
        title={a.hash}
      >
        <ExternalLink className="size-3.5" />
      </a>
    </li>
  )
}

function OperationIcon({ op, failed }: { op: string; failed: boolean }) {
  if (failed) return <XCircle className="size-4 text-destructive" />
  switch (op) {
    case 'send':
      return <ArrowUp className="size-4 text-destructive" />
    case 'receive':
      return <ArrowDown className="size-4 text-emerald-400" />
    case 'trade':
      return <ArrowUpDown className="size-4 text-sky-400" />
    case 'approve':
      return <ShieldCheck className="size-4 text-amber-400" />
    case 'mint':
      return <Sparkles className="size-4 text-emerald-400" />
    case 'burn':
      return <Flame className="size-4 text-destructive" />
    case 'deposit':
      return <Wallet className="size-4 text-sky-400" />
    case 'withdraw':
      return <TrendingDown className="size-4 text-amber-400" />
    default:
      return <CheckCircle2 className="size-4 text-muted-foreground" />
  }
}

interface TxSummary {
  title: string
  detail: string | null
}

function describeTx(tx: ZerionTransaction, self: string): TxSummary {
  const a = tx.attributes
  const op = a.operation_type
  const transfers = a.transfers.filter((t) => !!t.fungible_info?.symbol)
  const out = transfers.find((t) => t.direction === 'out')
  const inc = transfers.find((t) => t.direction === 'in')

  // Zerion occasionally emits empty / unknown operation_type; don't crash.
  const verb = op ? op[0]!.toUpperCase() + op.slice(1) : 'Tx'

  if (op === 'trade' && out && inc) {
    return {
      title: `${verb} ${formatTransfer(out)} → ${formatTransfer(inc)}`,
      detail: `nonce ${a.nonce}`,
    }
  }

  if (op === 'send' && out) {
    return {
      title: `${verb} ${formatTransfer(out)}`,
      detail: `→ ${short(a.sent_to)}`,
    }
  }

  if (op === 'receive' && inc) {
    return {
      title: `${verb} ${formatTransfer(inc)}`,
      detail: `← ${short(a.sent_from)}`,
    }
  }

  if (op === 'approve' && transfers[0]) {
    const t = transfers[0]
    const counterparty =
      t.recipient.toLowerCase() === self.toLowerCase() ? t.sender : t.recipient
    return {
      title: `${verb} ${t.fungible_info?.symbol ?? 'token'}`,
      detail: `to ${short(counterparty)}`,
    }
  }

  return {
    title: verb,
    detail: a.transfers.length
      ? `${a.transfers.length} transfer${a.transfers.length === 1 ? '' : 's'}`
      : null,
  }
}

function formatTransfer(t: ZerionTransaction['attributes']['transfers'][0]): string {
  const sym = t.fungible_info?.symbol ?? 'token'
  const amt = t.quantity.float
  const usd = t.value
  const amtStr = amt > 0 && amt < 0.01 ? '<0.01' : trimAmount(amt)
  if (usd != null && usd > 0) {
    return `${amtStr} ${sym} (${formatUsd(usd)})`
  }
  return `${amtStr} ${sym}`
}

function trimAmount(n: number): string {
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1000) {
    return sign + abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }
  if (abs >= 1) return sign + abs.toFixed(2)
  return sign + abs.toFixed(4)
}

function formatUsd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n < 1 ? 4 : 2,
  })
}

function explorerLink(chain: string, hash: string): string {
  switch (chain) {
    case 'ethereum':
      return `https://etherscan.io/tx/${hash}`
    case 'base':
      return `https://basescan.org/tx/${hash}`
    case 'arbitrum':
      return `https://arbiscan.io/tx/${hash}`
    case 'optimism':
      return `https://optimistic.etherscan.io/tx/${hash}`
    case 'polygon':
      return `https://polygonscan.com/tx/${hash}`
    case 'binance-smart-chain':
      return `https://bscscan.com/tx/${hash}`
    case 'avalanche':
      return `https://snowtrace.io/tx/${hash}`
    case 'zksync':
      return `https://explorer.zksync.io/tx/${hash}`
    case 'scroll':
      return `https://scrollscan.com/tx/${hash}`
    case 'linea':
      return `https://lineascan.build/tx/${hash}`
    case 'gnosis':
      return `https://gnosisscan.io/tx/${hash}`
    default:
      return `https://etherscan.io/tx/${hash}`
  }
}

function prettyChain(chain: string): string {
  if (!chain) return 'Unknown'
  if (chain === 'binance-smart-chain') return 'BNB'
  if (chain === 'polygon-zkevm') return 'Polygon zkEVM'
  return chain[0]!.toUpperCase() + chain.slice(1)
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const sec = Math.floor((now - then) / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}
